// Builds the customerAddress* fields for the registrationSubmission dataLayer
// event. Key names are the canonical customerData ids from
// global-events-watcher/helper/getCustomerData.js, so the browser event and the
// registration webhook carry the same keys.
//
// Source order: the address answered in this registration's form, then the
// stored profile address (the webhook's only source). This is the reverse of
// customer name and phone in ProductDetails, which prefer the profile.
//
// A form can capture an address two ways:
// - an ADDRESS question, whose answer is { line1, line2, city, state, zip,
//   country }. The server replaces the whole stored address with it, so it is
//   used on its own;
// - separate questions linked to one sub-property of an Address property (e.g.
//   a text question on "Address > City"), each answered with a plain string.
//   The server sets these as profile.address.<part>, merging them over the
//   stored address, so answers on the global property are laid over the
//   profile address the same way. The stored snapshot keeps only the parent
//   property id, so the sub-field is recovered from the form's moduleInfo,
//   which lines up with the snapshot entry for entry once content blocks are
//   dropped (FormDrawer getDataFromAnswers).
//
// Non-blank values pass through untrimmed, as the server stores them. Blank
// and whitespace-only values are dropped. A country on its own does not count
// as an address for an ADDRESS answer or the profile, because FormAddress
// always fills a geo default country, even when the shopper skips the
// question. A country picked in its own sub-property question is a real answer
// and counts.
//
// Pure and never throws: it runs inside the registration submission event,
// after the registration has already succeeded.

const ADDRESS_PARTS = [
  'line1',
  'line2',
  'city',
  'state',
  'zip',
  'country',
] as const;

type AddressPart = (typeof ADDRESS_PARTS)[number];
type Address = Partial<Record<AddressPart, string>>;

const OUTPUT_KEYS: Record<AddressPart, string> = {
  line1: 'customerAddressLine1',
  line2: 'customerAddressLine2',
  city: 'customerAddressCity',
  state: 'customerAddressState',
  zip: 'customerAddressZip',
  country: 'customerAddressCountry',
};

const ADDRESS_QUESTION = 'address';
const CONTENT_BLOCK_QUESTION = 'content-block';

// Lower rank wins. The global Address property is what the webhook reads, so
// it outranks addresses captured into a brand's custom property.
const RANK_GLOBAL_WHOLE = 0;
const RANK_GLOBAL_SPLIT = 1;
const RANK_OTHER_WHOLE = 2;
const RANK_OTHER_SPLIT = 3;

const isAddressPart = (value: unknown): value is AddressPart =>
  typeof value === 'string' &&
  (ADDRESS_PARTS as readonly string[]).includes(value);

const toValue = (value: unknown): string | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string' && value.trim() !== '') return value;
  return undefined;
};

const pickParts = (raw: unknown): Address => {
  const address: Address = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return address;
  for (const part of ADDRESS_PARTS) {
    const value = toValue((raw as Record<string, unknown>)[part]);
    if (value !== undefined) address[part] = value;
  }
  return address;
};

const toAddress = (raw: unknown, allowCountryOnly = false): Address | null => {
  const address = pickParts(raw);
  const provided = ADDRESS_PARTS.some(
    (part) =>
      address[part] !== undefined && (allowCountryOnly || part !== 'country')
  );
  return provided ? address : null;
};

const isWholeAddressAnswer = (answer: unknown): boolean =>
  !!answer &&
  typeof answer === 'object' &&
  !Array.isArray(answer) &&
  ADDRESS_PARTS.some((part) => part in (answer as Record<string, unknown>));

// Returns the question behind a snapshot entry only when the entry still
// matches it, so a form edited since the snapshot was taken is ignored rather
// than mis-mapped.
const getMatchingQuestion = (question: any, entry: any): any => {
  if (!question || !entry) return null;
  const questionText = typeof question.text === 'string' ? question.text : '';
  const propertyId = question.property?._id || '';
  return entry.question === questionText && entry.property === propertyId
    ? question
    : null;
};

const pickFormAddress = (
  responseWithProperty: unknown,
  moduleInfo: unknown,
  profileAddress: unknown
): Address | null => {
  if (!Array.isArray(responseWithProperty)) return null;

  const questions = Array.isArray(moduleInfo)
    ? moduleInfo.filter(
        (question: any) => question?.type !== CONTENT_BLOCK_QUESTION
      )
    : [];
  const aligned = questions.length === responseWithProperty.length;

  const candidates: { rank: number; address: Address }[] = [];
  const splitByProperty = new Map<
    string,
    { isGlobal: boolean; parts: Address }
  >();

  responseWithProperty.forEach((entry: any, index: number) => {
    if (!entry || typeof entry !== 'object') return;

    const question = aligned
      ? getMatchingQuestion(questions[index], entry)
      : null;
    const property = question?.property;
    const isGlobal = property?.global === true;

    const isAddressQuestion = question
      ? question.type === ADDRESS_QUESTION
      : isWholeAddressAnswer(entry.answer);
    if (isAddressQuestion) {
      const address = toAddress(entry.answer);
      if (address) {
        candidates.push({
          rank: isGlobal ? RANK_GLOBAL_WHOLE : RANK_OTHER_WHOLE,
          address,
        });
      }
      return;
    }

    if (!question?.subProperty || !property?._id) return;
    const subProperties = Array.isArray(property.subProperties)
      ? property.subProperties
      : [];
    const part = subProperties.find(
      (sub: any) => sub?._id === question.subProperty
    )?.name;
    if (!isAddressPart(part)) return;
    const value = toValue(entry.answer);
    if (value === undefined) return;

    let split = splitByProperty.get(property._id);
    if (!split) {
      split = { isGlobal, parts: {} };
      splitByProperty.set(property._id, split);
    }
    split.parts[part] = value;
  });

  splitByProperty.forEach((split) => {
    // Global split answers merge over the stored address, the same dot-path
    // update the server applies (consumer-app-microservice routes/form). A
    // brand's custom property never reaches profile.address, so its parts
    // stand alone.
    const parts = split.isGlobal
      ? { ...pickParts(profileAddress), ...split.parts }
      : split.parts;
    const address = toAddress(parts, true);
    if (address) {
      candidates.push({
        rank: split.isGlobal ? RANK_GLOBAL_SPLIT : RANK_OTHER_SPLIT,
        address,
      });
    }
  });

  // Array.prototype.sort is stable, so form order breaks ties within a rank.
  candidates.sort((a, b) => a.rank - b.rank);
  return candidates.length ? candidates[0].address : null;
};

// Every key is always returned, undefined when there is no value. GTM merges
// each dataLayer push into one data model, so a key left out would keep the
// value an earlier event set (e.g. registrationInitiation's profile address
// under a different form address at registrationSubmission). An undefined
// value overwrites it and is invisible to JSON and GTM variables.
const emptyCustomerAddressData = (): Record<string, string | undefined> => {
  const customerAddressData: Record<string, string | undefined> = {
    customerAddress: undefined,
  };
  for (const part of ADDRESS_PARTS) {
    customerAddressData[OUTPUT_KEYS[part]] = undefined;
  }
  return customerAddressData;
};

const buildCustomerAddressData = (args: {
  responseWithProperty?: unknown;
  moduleInfo?: unknown;
  profileAddress?: unknown;
}): Record<string, string | undefined> => {
  try {
    const customerAddressData = emptyCustomerAddressData();
    const address =
      pickFormAddress(
        args.responseWithProperty,
        args.moduleInfo,
        args.profileAddress
      ) || toAddress(args.profileAddress);
    if (!address) return customerAddressData;

    // Non-blank parts joined with ', ', like FormDrawer's ADDRESS response
    // text. The webhook's getFullAddress (eventhub-events fetchData.ts) builds
    // the same string whenever line1 is present; without line1 its string
    // starts with ', '.
    customerAddressData.customerAddress = ADDRESS_PARTS.map(
      (part) => address[part]
    )
      .filter(Boolean)
      .join(', ');
    for (const part of ADDRESS_PARTS) {
      customerAddressData[OUTPUT_KEYS[part]] = address[part];
    }
    return customerAddressData;
  } catch {
    return emptyCustomerAddressData();
  }
};

export default buildCustomerAddressData;
